import { Suspense, lazy, useCallback, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { hasCoPowers, initialsForUser } from '@cosvision/shared';
import { useAuthStore } from '../../store/auth-store.js';
import { useAiDrawerStore } from '../../store/ai-drawer-store.js';
import { useInboxUnreadQuery as useNotificationsUnreadQuery } from '../../hooks/useInbox.js';
import { useInboxUnreadQuery as useMessagesUnreadQuery } from '../../hooks/useMessages.js';
import { useRegistrationsQuery } from '../../hooks/useAuth.js';
import { useTaskUpdatesNewCountQuery } from '../../hooks/useTaskUpdatesFeed.js';
import { useAndroidBackButton } from '../../hooks/useAndroidBackButton.js';
import { useMobileTopBarStore } from '../../store/mobile-topbar-store.js';
import { NavIcon, type IconName } from './Sidebar.js';
import { BellIcon } from '../notifications/InboxBell.js';
import { MobileInboxSheet } from '../notifications/MobileInboxSheet.js';
import { ToastHost } from '../ui/ToastHost.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { AppLockScreen } from '../auth/AppLockScreen.js';

const AICopilotDrawer = lazy(() =>
  import('../ai/AICopilotDrawer.js').then((m) => ({ default: m.AICopilotDrawer })),
);
const TaskOverlayHost = lazy(() =>
  import('../tasks/TaskOverlayHost.js').then((m) => ({ default: m.TaskOverlayHost })),
);

/** Longest-prefix-first route → top-bar title map (M3 refines per surface). */
const TITLE_MAP: readonly [string, string][] = [
  ['/entry/import', 'Bulk Import'],
  ['/entry', 'Data Entry'],
  ['/funds', 'Fund Ledger'],
  ['/fdr', 'FDR / Savings'],
  ['/reports', 'Reports'],
  ['/settings', 'Settings'],
  ['/audit', 'Audit Log'],
  ['/ai-knowledge', 'AI Knowledge'],
  ['/my-work', 'My Work'],
  ['/tasks', 'Boards'],
  ['/overview', 'Command Overview'],
  ['/task-updates', 'Task Updates'],
  ['/people', 'People'],
  ['/officers', 'Officer'],
  ['/messages', 'Messages'],
  ['/approvals', 'Approvals'],
  ['/co-orders', "CO's Orders"],
  ['/more', 'More'],
];

function titleForPath(pathname: string): string {
  if (pathname === '/') return 'Dashboard';
  for (const [prefix, title] of TITLE_MAP) {
    if (pathname.startsWith(prefix)) return title;
  }
  return 'Odysseus';
}

interface TabSpec {
  key: string;
  label: string;
  icon: IconName;
  /** Route target; mutually exclusive with onPress. */
  to?: string;
  /** Custom active test (defaults to pathname === to). */
  isActive?: (pathname: string, search: string) => boolean;
  onPress?: () => void;
  badge?: number;
}

const tabBase =
  'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl pb-1.5 pt-2 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bn-300';

function TabBadgeDot({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute right-1/2 top-1 inline-flex min-w-[1rem] translate-x-3.5 items-center justify-center rounded-full bg-rag-red px-1 text-[9px] font-bold leading-4 text-white ring-2 ring-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Top sliding indicator that marks the active tab. */
function TabIndicator({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'absolute inset-x-4 top-0 h-[3px] rounded-full bg-bn-700 transition-opacity duration-200',
        active ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

function TabBar({ tabs }: { tabs: TabSpec[] }) {
  const location = useLocation();
  return (
    <nav
      aria-label="Primary"
      className="border-t border-slate-200/80 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch gap-0.5 px-1.5 py-1">
        {tabs.map((tab) => {
          const badge = tab.badge ?? 0;
          if (tab.to !== undefined) {
            const active = tab.isActive
              ? tab.isActive(location.pathname, location.search)
              : location.pathname === tab.to;
            return (
              <NavLink
                key={tab.key}
                to={tab.to}
                aria-label={badge > 0 ? `${tab.label} — ${String(badge)} unread` : tab.label}
                aria-current={active ? 'page' : undefined}
                className={clsx(tabBase, active ? 'text-bn-700' : 'text-slate-400')}
              >
                <TabIndicator active={active} />
                <NavIcon name={tab.icon} />
                <span className="max-w-full truncate">{tab.label}</span>
                <TabBadgeDot count={badge} />
              </NavLink>
            );
          }
          return (
            <button
              key={tab.key}
              type="button"
              onClick={tab.onPress}
              aria-label={badge > 0 ? `${tab.label} — ${String(badge)} unread` : tab.label}
              className={clsx(tabBase, 'text-slate-400')}
            >
              <NavIcon name={tab.icon} />
              <span className="max-w-full truncate">{tab.label}</span>
              <TabBadgeDot count={badge} />
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function BackChevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/**
 * Mobile shell — bottom tab bar + slim top bar, rendered below `lg`
 * (android-app-plan §6.1). Tabs are links to EXISTING routes; no route
 * changes. The desktop AppShell tree is untouched at `lg+`.
 */
export function MobileShell({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const openAi = useAiDrawerStore((s) => s.open);
  const location = useLocation();
  const navigate = useNavigate();
  const [inboxOpen, setInboxOpen] = useState(false);
  // Stable closer — Sheet registers/unregisters effects keyed on onClose, so
  // an inline arrow would churn its listeners on every shell re-render.
  const closeInbox = useCallback(() => setInboxOpen(false), []);

  // Per-surface top-bar override (set via useMobileTopBar). Ignored the instant
  // the route changes so a page that sets nothing never inherits a stale header.
  const topBar = useMobileTopBarStore((s) => s.config);
  const topBarPath = useMobileTopBarStore((s) => s.pathname);
  const cfg = topBarPath === location.pathname ? topBar : {};
  const title = cfg.title ?? titleForPath(location.pathname);
  const subtitle = cfg.subtitle;
  const showBack = cfg.back === true;
  const hideActions = cfg.hideActions === true;
  const hideHeader = cfg.hideHeader === true;
  const fullBleed = cfg.fullBleed === true;
  const handleBack = useCallback(() => {
    if (cfg.onBack) cfg.onBack();
    else navigate(-1);
  }, [cfg, navigate]);

  useAndroidBackButton();

  const isCo = hasCoPowers(user);
  const isOfficer = user?.role === 'OFFICER';
  const isOperator = user?.role === 'DATA_ENTRY';
  const hasInbox = isCo || isOfficer;

  // Badge sources — the same queries that drive the desktop sidebar badges.
  const notifQ = useNotificationsUnreadQuery(hasInbox);
  const notifUnread = notifQ.data?.total ?? 0;
  const messagesQ = useMessagesUnreadQuery(hasInbox);
  const messagesUnread = messagesQ.data?.total ?? 0;
  const pendingQ = useRegistrationsQuery(isCo);
  const pendingCount = pendingQ.data?.length ?? 0;
  const updatesNewQ = useTaskUpdatesNewCountQuery(isCo);
  const updatesNew = updatesNewQ.data ?? 0;

  let tabs: TabSpec[] = [];
  if (isCo) {
    tabs = [
      { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', to: '/' },
      { key: 'tasks', label: 'Tasks', icon: 'overview', to: '/overview' },
      {
        key: 'messages',
        label: 'Messages',
        icon: 'inbox',
        to: '/messages',
        isActive: (p) => p.startsWith('/messages'),
        badge: messagesUnread,
      },
      { key: 'ai', label: 'AI', icon: 'ai', onPress: openAi },
      {
        key: 'more',
        label: 'More',
        icon: 'more',
        to: '/more',
        badge: pendingCount + updatesNew,
      },
    ];
  } else if (isOfficer) {
    tabs = [
      { key: 'my-work', label: 'My Work', icon: 'tasks', to: '/my-work' },
      {
        key: 'boards',
        label: 'Boards',
        icon: 'board',
        to: '/tasks',
        isActive: (p) => p.startsWith('/tasks'),
      },
      {
        key: 'messages',
        label: 'Messages',
        icon: 'inbox',
        to: '/messages',
        isActive: (p) => p.startsWith('/messages'),
        badge: messagesUnread,
      },
      {
        key: 'updates',
        label: 'Updates',
        icon: 'updates',
        onPress: () => setInboxOpen(true),
        badge: notifUnread,
      },
      { key: 'more', label: 'More', icon: 'more', to: '/more' },
    ];
  } else if (isOperator) {
    tabs = [
      {
        key: 'entry',
        label: 'Entry',
        icon: 'entry',
        to: '/entry',
        isActive: (p, s) => p === '/entry' && !s.includes('view=list'),
      },
      {
        key: 'my-entries',
        label: 'My Entries',
        icon: 'audit',
        to: '/entry?view=list',
        isActive: (p, s) => p === '/entry' && s.includes('view=list'),
      },
      { key: 'more', label: 'More', icon: 'more', to: '/more' },
    ];
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-slate-50">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-bn-800 focus:px-3 focus:py-1.5 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to main content
      </a>

      {/* Slim top bar: optional back · screen title (+ subtitle) · bell + avatar.
          Immersive surfaces (chat thread) hide it entirely via hideHeader and
          render their own header instead. */}
      <header
        className={clsx(
          'flex items-center gap-2 border-b border-slate-200/80 bg-white/95 px-3 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] backdrop-blur supports-[backdrop-filter]:bg-white/80',
          hideHeader && 'hidden',
        )}
      >
        {showBack ? (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="-ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-600 transition active:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bn-300"
          >
            <BackChevron />
          </button>
        ) : null}
        <div className={clsx('min-w-0 flex-1', showBack ? '' : 'pl-1')}>
          <h1 className="truncate text-[17px] font-semibold leading-tight text-slate-900">
            {title}
          </h1>
          {subtitle ? (
            <p className="truncate text-[12px] leading-tight text-slate-500">{subtitle}</p>
          ) : null}
        </div>
        <div className={clsx('flex shrink-0 items-center gap-1', hideActions && 'hidden')}>
          {hasInbox ? (
            <button
              type="button"
              onClick={() => setInboxOpen(true)}
              aria-label={notifUnread > 0 ? `Inbox — ${String(notifUnread)} unread` : 'Inbox'}
              className="relative grid h-10 w-10 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300"
            >
              <BellIcon />
              {notifUnread > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute right-0.5 top-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-rag-red px-1 text-[10px] font-bold leading-none text-white"
                >
                  {notifUnread > 99 ? '99+' : notifUnread}
                </span>
              ) : null}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/more')}
            aria-label="Profile and more"
            className="grid h-10 w-10 place-items-center rounded-full focus:outline-none focus:ring-2 focus:ring-bn-300"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bn-700 text-[11px] font-semibold text-white">
              {user ? initialsForUser(user) : '?'}
            </span>
          </button>
        </div>
      </header>

      <main
        id="main"
        className={clsx(
          'relative min-w-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          fullBleed ? 'p-0' : 'p-4',
        )}
        tabIndex={-1}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      <TabBar tabs={tabs} />

      {hasInbox ? (
        <MobileInboxSheet open={inboxOpen} onClose={closeInbox} alertsEnabled={isCo} />
      ) : null}

      {isCo ? (
        <Suspense fallback={null}>
          <AICopilotDrawer />
        </Suspense>
      ) : null}
      {isCo || isOfficer ? (
        <Suspense fallback={null}>
          <TaskOverlayHost />
        </Suspense>
      ) : null}

      {/* Toasts sit above the 3.5rem tab bar + safe area (§6.3). */}
      <ToastHost bottomOffsetClass="bottom-[calc(4.5rem+env(safe-area-inset-bottom))]" />
      <AppLockScreen />
    </div>
  );
}
